import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const HERO_BG = "https://images.unsplash.com/photo-1536440136628-849c177e76a1?q=80&w=1920&auto=format&fit=crop";
const GOOGLE_CLIENT_ID = "1071862273705-rgcf0s7jj35l1g4jio8ehpg20rhh05ac.apps.googleusercontent.com";

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const googleBtnRef = useRef(null);
  
  const redirectFrom = location.state?.from;
  const [mode, setMode] = useState(redirectFrom ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingForgot, setSendingForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(redirectFrom || "/", { replace: true });
    }
  }, [user, navigate, redirectFrom]);

  // Initialize Google Identity Services (GSI)
  useEffect(() => {
    const handleCredentialResponse = async (response) => {
      if (!response?.credential) return;
      setGoogleLoading(true);
      try {
        const { data } = await api.post("/auth/google", {
          credential: response.credential
        });
        setUser(data);
        toast.success(`¡Bienvenido a La Caja LGBT, ${data.name}!`);
        navigate(redirectFrom || "/", { replace: true });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Error al autenticar con Google");
      } finally {
        setGoogleLoading(false);
      }
    };

    const setupGoogle = () => {
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true
          });
        } catch (e) {
          console.warn("GSI init warning:", e);
        }
      }
    };

    setupGoogle();
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        setupGoogle();
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [navigate, redirectFrom, setUser]);

  const handleGoogleLogin = () => {
    setGoogleLoading(true);

    // 1. Try Google OAuth2 Token Client Popup
    if (window.google?.accounts?.oauth2) {
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "email profile openid",
          callback: async (tokenResponse) => {
            if (tokenResponse?.access_token) {
              try {
                const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
                  headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
                });
                const profile = await res.json();
                const { data } = await api.post("/auth/google", {
                  email: profile.email,
                  name: profile.name,
                  picture: profile.picture
                });
                setUser(data);
                toast.success(`¡Bienvenido a La Caja LGBT, ${data.name}!`);
                navigate(redirectFrom || "/", { replace: true });
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Error al iniciar sesión con Google");
              } finally {
                setGoogleLoading(false);
              }
            } else {
              setGoogleLoading(false);
            }
          },
          error_callback: () => {
            setGoogleLoading(false);
            // Fallback to Google One Tap
            if (window.google?.accounts?.id) {
              window.google.accounts.id.prompt();
            }
          }
        });
        tokenClient.requestAccessToken();
        return;
      } catch (e) {
        console.warn("Google popup error:", e);
      }
    }

    // 2. Fallback to Google One Tap Prompt
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          setGoogleLoading(false);
          toast.error("Por favor permite la ventana emergente de Google o ingresa con tu correo y contraseña.");
        }
      });
    } else {
      setGoogleLoading(false);
      toast.error("Cargando servicios de Google... Inténtalo de nuevo.");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !forgotEmail.includes("@")) {
      toast.error("Introduce un correo electrónico válido");
      return;
    }
    setSendingForgot(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
      toast.success(data.message || "Solicitud procesada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al solicitar restablecimiento");
    } finally {
      setSendingForgot(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      toast.success(mode === "login" ? `¡Bienvenido de nuevo, ${data.name}!` : `¡Cuenta creada exitosamente! Bienvenido, ${data.name}`);
      navigate(redirectFrom || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block w-1/2 relative">
        <img src={HERO_BG} alt="La Caja LGBT Cine" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/40 via-[#050505]/80 to-[#050505]" />
        <div className="absolute bottom-16 left-12 max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E11D48]/20 border border-[#E11D48]/40 text-[#E11D48] text-xs font-semibold uppercase tracking-wider mb-4">
            ★ Cine & Series LGBTQ+
          </div>
          <h2 className="font-heading text-4xl font-bold tracking-tight text-white leading-tight">
            El mayor catálogo audiovisual <span className="prism-text">LGBTQ+</span>
          </h2>
          <p className="text-zinc-400 mt-3 text-sm leading-relaxed">
            Descubre películas, series, cortometrajes y documentales de todo el mundo en streaming gratis y en alta definición.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#050505]">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-3 h-3 rounded-full bg-[#E11D48] shadow-[0_0_10px_#E11D48]" />
            <h1 className="font-heading text-2xl font-bold tracking-tight text-white">
              LA CAJA <span className="prism-text">LGBT</span>
            </h1>
          </div>
          <p className="text-zinc-400 text-sm mb-6">
            {mode === "login" ? "Inicia sesión con tu cuenta" : "Crea tu cuenta gratuita en segundos"}
          </p>

          {redirectFrom && (
            <div className="mb-6 p-3.5 bg-[#E11D48]/10 border border-[#E11D48]/30 rounded-xl text-xs text-rose-300 font-medium leading-relaxed shadow-lg shadow-[#E11D48]/5">
              🎬 Regístrate gratis para ver el contenido seleccionado y guardar tus títulos favoritos en tu lista personal.
            </div>
          )}

          {/* Botón de Google Nativo */}
          <button
            data-testid="google-login-button"
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading || loading}
            className="w-full border border-white/20 bg-white/5 hover:bg-white/10 rounded-xl py-3 px-4 flex items-center justify-center gap-3 text-white transition-all shadow-sm hover:border-white/30 disabled:opacity-50"
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
            )}
            <span className="text-sm font-semibold">
              {googleLoading ? "Conectando con Google..." : mode === "login" ? "Continuar con Google" : "Registrarse con Google"}
            </span>
          </button>

          <div ref={googleBtnRef} className="hidden" />

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-zinc-500 uppercase tracking-widest font-mono">o con correo</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-medium">{t("name")}</label>
                <input
                  data-testid="auth-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Tu nombre completo"
                  className="w-full bg-zinc-900/80 border border-white/15 rounded-lg px-3.5 py-2.5 mt-1 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#E11D48] transition-colors"
                />
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-medium">{t("phone")}</label>
                <input
                  data-testid="auth-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t("phonePlaceholder")}
                  className="w-full bg-zinc-900/80 border border-white/15 rounded-lg px-3.5 py-2.5 mt-1 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#E11D48] transition-colors"
                />
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-medium">{t("email")}</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                placeholder="ejemplo@correo.com"
                className="w-full bg-zinc-900/80 border border-white/15 rounded-lg px-3.5 py-2.5 mt-1 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-medium">{t("password")}</label>
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(true)}
                    className="text-[11px] text-zinc-400 hover:text-[#E11D48] transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <input
                data-testid="auth-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                placeholder="••••••••"
                className="w-full bg-zinc-900/80 border border-white/15 rounded-lg px-3.5 py-2.5 mt-1 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#E11D48] text-white py-3 rounded-xl font-semibold hover:bg-[#BE123C] transition-colors disabled:opacity-50 shadow-lg shadow-[#E11D48]/20 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t("processing")}</span>
                </>
              ) : (
                mode === "login" ? t("signIn") : t("registerBtn")
              )}
            </button>
          </form>

          <p className="text-center text-sm text-zinc-400 mt-6">
            {mode === "login" ? "¿No tienes una cuenta aún?" : "¿Ya tienes una cuenta registrada?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#E11D48] hover:underline font-semibold"
            >
              {mode === "login" ? "Regístrate gratis" : "Iniciar Sesión"}
            </button>
          </p>
        </div>
      </div>

      {/* MODAL RECUPERAR CONTRASEÑA */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#121214] border border-white/15 rounded-2xl max-w-md w-full p-6 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E11D48] via-purple-600 to-[#E11D48]" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-white font-heading flex items-center gap-2">
                <span>🔑</span> Recuperar Contraseña
              </h3>
              <button
                onClick={() => { setShowForgotModal(false); setForgotSent(false); }}
                className="text-zinc-400 hover:text-white text-sm"
              >
                ✕
              </button>
            </div>

            {forgotSent ? (
              <div className="text-center py-4 space-y-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto text-xl font-bold">
                  ✓
                </div>
                <h4 className="text-sm font-bold text-white">¡Enlace de recuperación enviado!</h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Si la dirección <strong className="text-zinc-200">{forgotEmail}</strong> está registrada, recibirás las instrucciones en tu bandeja de entrada o spam en unos instantes.
                </p>
                <button
                  onClick={() => { setShowForgotModal(false); setForgotSent(false); }}
                  className="mt-4 w-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold py-2.5 rounded-lg transition-colors"
                >
                  Entendido / Cerrar
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Ingresa tu correo electrónico y te enviaremos un enlace seguro para restablecer tu contraseña.
                </p>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-1">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="ejemplo@correo.com"
                    className="w-full bg-zinc-900 border border-white/15 rounded-lg px-3.5 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#E11D48]"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold py-2.5 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={sendingForgot}
                    className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-lg shadow-[#E11D48]/20"
                  >
                    {sendingForgot ? "Enviando..." : "Enviar Enlace"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

